import React, { FunctionComponent, useEffect, useState } from 'react'
import { Box, Button, TextareaAutosize, TextField } from '@mui/material'
import { useParams } from 'infrastructure/routing'
import {
  ContentServiceFlowStatus,
  FlowListItem,
  FlowResult,
  RequestError,
} from './types'
import {
  getWorkerFlowResult,
  isExternalUrlsAccumulatorResponse,
  isGDriveResponse,
  isLessonMappingResponse,
  isPdfTreeTranslateDiscoveryResponse,
  isPdfTranslationResultResponse,
  isRequestError,
  isTreeCreateResponse,
  isTreeOperationsResponse,
  isTreeStructureResponse,
  isUrlVerificationResponse,
  isUrlReplacementResponse,
} from './utility'
import { PollingFlowStatus } from './PollingFlowStatus'
import { BorderedContainer } from '../utility/Containers'
import { GDriveFilesResponse } from './GDriveFilesResponse'
import { TreeOperationsResult } from './TreeOperationsResult'
import { Table } from 'shared/ui/Table/Table'
import { useAllFlows, useNavigateToFlow } from './hooks'
import { TimeAgo } from 'shared/ui'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { flowListAtom } from './atoms'
import { Column } from 'shared/ui/Table/types'
import { LessonMappingsResultView } from 'domains/scope-and-sequence/features/lesson-mappings/FlowResultView'
import { ScopeSequenceTreeStructureResponse } from './ScopeSequenceTreeStructureResponse'
import { ScopeSequenceTreeCreateResponse } from './ScopeSequenceTreeCreateResponse'
import { ExternalUrlsAccumulatorResponse } from './ExternalUrlsAccumulatorResponse'
import { PdfTreeTranslateDiscoveryResponse } from './PdfTreeTranslateDiscoveryResponse'
import { UrlVerificationResponse } from './UrlVerificationResponse'
import { UrlReplacementResponse } from './UrlReplacementResponse'
import { PdfTranslationResultResponse } from './PdfTranslationResultResponse'

export function FlowResultRoute() {
  const { flowId } = useParams()
  const navigate = useNavigateToFlow()

  if (!flowId) return <Box>We need a flow id</Box>

  return (
    <Box>
      <Box>
        <Button onClick={() => navigate()}>Clear</Button>
      </Box>
      <FlowIdView flowId={flowId} />
      <AllFlowIds />
    </Box>
  )
}

export function FlowIdInputRoute() {
  const [flowId, setFlowId] = useState('')
  const navigate = useNavigateToFlow()
  return (
    <Box>
      <Box>To view flow results input flow ID</Box>
      <Box>
        <TextField
          value={flowId}
          onChange={(e) => setFlowId(e.target.value)}
          label="Flow ID"
        />
        <Button onClick={() => navigate(flowId)}>Go</Button>
      </Box>
      <AllFlowIds />
    </Box>
  )
}

function AllFlowIds() {
  const allFlows = useAllFlows()
  const refreshFlows = useSetAtom(flowListAtom)
  const navigate = useNavigateToFlow()
  if (!allFlows) return null

  const cols: Column<FlowListItem>[] = [
    {
      title: 'Flow Description',
      rowContent: ({ description }) => description,
    },
    {
      title: 'Created',
      rowContent: ({ createdAt }) => <TimeAgo date={createdAt} />,
    },
    {
      title: 'Completed',
      rowContent: ({ completed }) => String(completed),
    },
    {
      title: 'Select',
      rowContent: ({ flowId }) => {
        return <Button onClick={() => navigate(flowId)}>Select</Button>
      },
    },
  ]
  return (
    <Table
      title="All Flows"
      toolBarItems={[
        <Button key="refresh" onClick={() => refreshFlows()}>
          Refresh
        </Button>,
      ]}
      items={allFlows}
      columns={cols}
    />
  )
}

export function FlowIdView<TResult extends FlowResult>({
  flowId,
  CompletedFlowComponent = () => null,
  FlowResultComponent = FlowResults,
  onFlowPoll,
  onFlowResult,
}: {
  flowId?: string
  CompletedFlowComponent?: FunctionComponent<{ result: TResult }>
  FlowResultComponent?: FunctionComponent<{ flowResult: TResult | undefined }>
  onFlowPoll?: (
    flowResults: ContentServiceFlowStatus | RequestError | undefined,
  ) => void
  onFlowResult?: (result: TResult) => void
}) {
  const [flowStatus, setFlowStatus] = React.useState<
    ContentServiceFlowStatus | RequestError
  >()
  const [flowResult, setFlowResult] = useState<TResult>()
  const [error, setError] = useState<unknown>()

  useEffect(() => {
    if (!flowStatus) return
    onFlowPoll?.(flowStatus)
  }, [flowStatus, onFlowPoll])

  useEffect(() => {
    setFlowStatus(undefined)
  }, [flowId])

  useEffect(() => {
    if (!flowResult) return
    onFlowResult?.(flowResult)
  }, [flowResult, onFlowResult])

  useEffect(() => {
    if (!flowStatus) {
      setError(undefined)
      setFlowResult(undefined)
      return
    }
    if (isRequestError(flowStatus)) {
      setFlowResult(undefined)
      setError(flowStatus.errorText)
      return
    }
    const flowId = flowStatus.meta.parameters.flow_id
    if (!flowStatus.data.completed || !flowId) {
      setFlowResult(undefined)
      return
    }
    getWorkerFlowResult<TResult>(flowId).then(({ result, error }) => {
      setFlowResult(result)
      setError(error)
    })
  }, [flowStatus])

  return (
    <Box>
      <PollingFlowStatus flowId={flowId} onFlowPoll={setFlowStatus} />
      <FlowError error={error} />
      {flowResult && <CompletedFlowComponent result={flowResult} />}
      <FlowResultComponent flowResult={flowResult} />
    </Box>
  )
}

export function FlowResults<TResult extends FlowResult>({
  flowResult,
}: {
  flowResult: TResult | undefined
}) {
  if (!flowResult) return null

  return (
    <BorderedContainer>
      <Box>The results</Box>
      <FlowResultView result={flowResult} />
    </BorderedContainer>
  )
}

function FlowResultView({ result }: { result: FlowResult }) {
  if (!result) return null
  const { worker_type } = result.meta
  return (
    <Box>
      <Box>{worker_type}</Box>
      <ResultComponent result={result} />
    </Box>
  )
}

function ResultComponent({ result }: { result: FlowResult }) {
  const { controller_id } = result.meta
  if (isTreeOperationsResponse(result)) {
    return <TreeOperationsResult result={result.data} flowId={controller_id} />
  }
  if (isGDriveResponse(result)) {
    return <GDriveFilesResponse filesResponse={result} />
  }
  if (isLessonMappingResponse(result)) {
    return <LessonMappingsResultView data={result.data} />
  }
  if (isTreeStructureResponse(result)) {
    return <ScopeSequenceTreeStructureResponse data={result.data} />
  }
  if (isTreeCreateResponse(result)) {
    return <ScopeSequenceTreeCreateResponse data={result.data} />
  }
  if (isExternalUrlsAccumulatorResponse(result)) {
    return <ExternalUrlsAccumulatorResponse result={result} />
  }
  if (isPdfTreeTranslateDiscoveryResponse(result)) {
    return <PdfTreeTranslateDiscoveryResponse result={result} />
  }
  if (isUrlVerificationResponse(result)) {
    return <UrlVerificationResponse result={result} />
  }
  if (isUrlReplacementResponse(result)) {
    return <UrlReplacementResponse result={result} />
  }
  if (isPdfTranslationResultResponse(result)) {
    return <PdfTranslationResultResponse result={result} />
  }
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(result, null, 2))
  }
  return (
    <>
      <TextareaAutosize
        disabled={true}
        cols={100}
        maxRows={25}
        value={JSON.stringify(result, null, 2)}
      />
      <Button variant="outlined" onClick={handleCopy}>
        Copy
      </Button>
    </>
  )
}

export function FlowError({ error }: { error: unknown }) {
  if (!error) return null
  return <Box>{String(error)}</Box>
}
